// Firebase Functions v2 URL Configuration
// Environment-aware URL generation for Cloud Functions

// Environment detection
const isLocalDevelopment = (): boolean => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0';
};

const isProductionEnvironment = (): boolean => {
  const hostname = window.location.hostname;
  return hostname === 'aiprep.io' ||
         hostname === 'www.aiprep.io' ||
         hostname === 'app.aiprep.io';
};

// Project IDs for different environments
const PROJECT_IDS = {
  development: 'aiprep-465906',
  production: 'aiprep-prod',
} as const;

// Get the current project ID based on environment
const getProjectId = (): string => {
  if (isProductionEnvironment()) {
    return PROJECT_IDS.production;
  }
  return PROJECT_IDS.development;
};

// Get the base URL for Cloud Functions
const getCloudFunctionsBaseUrl = (): string => {
  if (isLocalDevelopment()) {
    // Local development - use emulator
    return `http://127.0.0.1:5001/${PROJECT_IDS.development}/us-central1`;
  }
  const projectId = getProjectId();
  return `https://us-central1-${projectId}.cloudfunctions.net`;
};

// List of all function names (for type safety)
export const FUNCTION_NAMES = [
  // Auth functions
  'getUserProfile',
  'updateUserPreferences',
  'saveUserSolution',
  'getUserSolutions',
  'getUserSolutionByProblem',
  'getAllUserSolutionsForProblem',
  'toggleCompletionStatus',
  'getCompletionStatus',
  'getAllCompletionStatus',
  'batchUpdateCompletionStatus',

  // Chat functions
  'getChatHistory',
  'sendChatMessage',
  'saveChatHistory',
  'chatHealth',

  // Problem functions
  'getProblems',
  'getProblemsBatch',
  'generateProblem',
  'searchOrGenerate',
  'getProblemById',
  'updateProblem',
  'deleteProblem',
  'getStats',
  'verifySolution',
  'verifyReview',
  'generateLanguageCode',
  'generateAlgorithmAnimation',
  'getAnimationUrl',
  'generateAlgorithmWalkthrough',
  'getAlgorithmWalkthroughs',

  // Collection functions
  'getCollections',
  'getSystemCollections',
  'getCollection',
  'createCollection',
  'createCollectionStream',  // SSE streaming endpoint for progress updates
  'createSystemCollection',
  'updateCollection',
  'deleteCollection',
  'updateCollectionProblems',
  'generateCollectionByAI',
  'updateCollectionOrder',
  'updateProblemSequence',

  // Pin functions
  'getUserPins',
  'updateUserPins',
  'pinCollection',
  'unpinCollection',
  'pinProblem',
  'unpinProblem',

  // Test functions
  'testSimple',
  'testDbSimple',
  'testFirestoreConnection',

  // Health functions
  'authHealth',
  'health',

  // Upload functions
  'uploadCsv',
  'processCsvBatch',
  'getUploadSession',
  'transcribeAudio',
  'uploadHealth',

  // Cache functions
  'warmCache',
  'cacheStatus',
  'clearCache',
  'invalidateProblem',
  'redisInfo',
  'cacheHealth',

  // Warmup function for frontend-triggered backend warming
  'warmup',

  // Attribution functions for marketing tracking
  'trackAttribution',
  'setUserAttribution',

  // Paywall functions
  'createCheckoutSession',
  'handleStripeWebhook',
  'getSubscriptionStatus',
  'cancelSubscription',
  'getUsageStats',
  'checkFeatureAccess',
] as const;

export type FunctionName = typeof FUNCTION_NAMES[number];

// Generate V2_FUNCTION_URLS dynamically based on environment
// This ensures production uses aiprep-prod and dev uses aiprep-465906
const generateFunctionUrls = (): Record<FunctionName, string> => {
  const baseUrl = getCloudFunctionsBaseUrl();
  const urls: Partial<Record<FunctionName, string>> = {};

  for (const name of FUNCTION_NAMES) {
    urls[name] = `${baseUrl}/${name}`;
  }

  return urls as Record<FunctionName, string>;
};

// Export the dynamically generated URLs
export const V2_FUNCTION_URLS = generateFunctionUrls();

// Helper to check if we should use v2 URLs (non-local environments)
export const shouldUseV2Functions = (): boolean => {
  return !isLocalDevelopment();
};

// Get the appropriate function URL
export const getFunctionUrl = (functionName: FunctionName | string): string => {
  const baseUrl = getCloudFunctionsBaseUrl();
  return `${baseUrl}/${functionName}`;
};

// Export environment helpers for use elsewhere
export const getEnvironmentProjectId = getProjectId;
export const getEnvironmentBaseUrl = getCloudFunctionsBaseUrl;

// Debug helper
export const logFunctionUrlConfig = (): void => {
  console.log('Function URL Configuration:', {
    isLocal: isLocalDevelopment(),
    isProduction: isProductionEnvironment(),
    projectId: getProjectId(),
    baseUrl: getCloudFunctionsBaseUrl(),
    hostname: window.location.hostname,
  });
};
